import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Inbox, LayoutDashboard, LogOut, Menu, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const items = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview" },
  { to: "/admin/submissions", icon: Inbox, label: "Form Submissions" },
];

export default function AdminLayout() {
  const { userDoc, user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const onLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/");
  };

  return (
    <div className="min-h-screen flex relative">
      {open && <div className="fixed inset-0 bg-black/30 z-30 md:hidden" onClick={() => setOpen(false)} />}

      <aside
        className={cn(
          "fixed md:sticky top-0 z-40 md:z-10 h-screen w-72 bg-retro-purple text-white border-r-[3px] border-retro-ink overflow-y-auto transition-transform",
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
        data-testid="admin-sidebar"
      >
        <div className="p-5 border-b-[3px] border-retro-ink flex items-center gap-2 bg-retro-purple">
          <Link to="/" className="flex items-center gap-2 text-white">
            <span className="w-9 h-9 grid place-items-center bg-retro-yellow text-retro-ink border-[2.5px] border-retro-ink rounded-lg shadow-retro-sm font-pixel text-[10px] rotate-[-4deg]">NC</span>
            <span className="font-heading text-lg uppercase">Admin</span>
          </Link>
          <button onClick={() => setOpen(false)} className="ml-auto md:hidden retro-btn p-1.5 bg-white text-retro-ink">
            <X size={14} />
          </button>
        </div>

        <nav className="p-3">
          <ul className="space-y-1">
            {items.map((it) => {
              const active = location.pathname === it.to;
              return (
                <li key={it.to}>
                  <Link
                    to={it.to}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg border-[2px] font-body text-sm transition-all",
                      active
                        ? "bg-retro-yellow text-retro-ink border-retro-ink shadow-retro-sm"
                        : "border-transparent hover:bg-white/10"
                    )}
                    data-testid={`admin-link-${it.label.toLowerCase().replace(/[ &]+/g, "-")}`}
                  >
                    <it.icon size={16} />
                    {it.label}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="mt-4 pt-4 border-t border-white/20">
            <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-lg border-[2px] border-transparent hover:border-white text-sm">
              Back to website
            </Link>
            <button onClick={onLogout} className="flex items-center gap-3 px-3 py-2 rounded-lg border-[2px] border-transparent hover:border-white text-sm w-full">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </nav>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-retro-bg/85 backdrop-blur-md border-b-[3px] border-retro-ink px-5 md:px-8 py-3 flex items-center justify-between gap-3">
          <button onClick={() => setOpen(true)} className="md:hidden retro-btn p-2 bg-white" data-testid="admin-menu-toggle">
            <Menu size={16} />
          </button>
          <div className="hidden md:flex items-center gap-2">
            <ShieldCheck className="text-retro-purple" />
            <p className="font-heading text-lg uppercase">Admin Responses</p>
            <span className="font-pixel text-[10px] text-retro-ink/60">// {location.pathname}</span>
          </div>
          <span className="font-body text-sm">{userDoc?.email || user?.email}</span>
        </header>
        <main className="p-5 md:p-8 max-w-7xl mx-auto" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
